import React, { useState, useEffect, useCallback, useRef } from 'react'
import { FolderOpen, Plus, FileText, Download, Link as LinkIcon, ExternalLink, Pencil, Trash2, Search, Share2 } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { resourceService } from '@/services/resourceService'
import { useToast } from '@/contexts/ToastContext'
import CreateResourceModal from '@/components/resources/CreateResourceModal'
import EditResourceModal from '@/components/resources/EditResourceModal'
import ShareResourceModal from '@/components/resources/ShareResourceModal'
import { Resource } from '@/types/resource'

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

type ResourcesTab = 'mine' | 'shared'

const ResourcesPage: React.FC = () => {
  const { showToast } = useToast()
  const [activeTab, setActiveTab] = useState<ResourcesTab>('mine')
  const [resources, setResources] = useState<Resource[]>([])
  const [loading, setLoading] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingResource, setEditingResource] = useState<Resource | null>(null)
  const [sharingResource, setSharingResource] = useState<Resource | null>(null)
  const [deletingResource, setDeletingResource] = useState<Resource | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [searchTitle, setSearchTitle] = useState('')
  const [selectedTag, setSelectedTag] = useState('')
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const fetchingRef = useRef(false)
  const [sharedResources, setSharedResources] = useState<Resource[]>([])
  const [sharedLoading, setSharedLoading] = useState(false)
  const [sharedLoaded, setSharedLoaded] = useState(false)

  const fetchResources = useCallback(async (filters?: { title?: string; tag?: string }) => {
    if (fetchingRef.current) return

    try {
      fetchingRef.current = true
      setLoading(true)
      const data = await resourceService.getMyResources(filters)
      setResources(data)
      if (!filters?.title && !filters?.tag) {
        setAvailableTags(Array.from(new Set(data.flatMap((r) => r.tags))).sort())
      }
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch resources'
      })
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [showToast])

  useEffect(() => {
    const activeFilters = { title: searchTitle || undefined, tag: selectedTag || undefined }
    const timer = setTimeout(
      () => fetchResources(activeFilters),
      searchTitle ? 300 : 0
    )
    return () => clearTimeout(timer)
  }, [searchTitle, selectedTag, fetchResources])

  const fetchSharedResources = useCallback(async () => {
    try {
      setSharedLoading(true)
      const data = await resourceService.getSharedWithMe()
      setSharedResources(data)
      setSharedLoaded(true)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch shared resources'
      })
    } finally {
      setSharedLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    if (activeTab === 'shared' && !sharedLoaded) {
      fetchSharedResources()
    }
  }, [activeTab, sharedLoaded, fetchSharedResources])

  const handleResourceCreated = () => {
    fetchResources({ title: searchTitle || undefined, tag: selectedTag || undefined })
  }

  const handleResourceUpdated = (updated: Resource) => {
    setResources((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
  }

  const handleDelete = async () => {
    if (!deletingResource) return

    try {
      setIsDeleting(true)
      await resourceService.deleteResource(deletingResource.id)
      setResources((prev) => prev.filter((r) => r.id !== deletingResource.id))
      showToast({ type: 'success', message: 'Resource deleted' })
      setDeletingResource(null)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete resource'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const renderResourceCard = (resource: Resource, actions: React.ReactNode) => (
    <Card key={resource.id} className="hover:shadow-md transition-shadow">
      <CardBody>
        <div className="flex items-start gap-3">
          {resource.type === 'link' ? (
            <LinkIcon className="w-8 h-8 text-teal-500 flex-shrink-0 mt-1" />
          ) : (
            <FileText className="w-8 h-8 text-primary-500 flex-shrink-0 mt-1" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {resource.title}
              </h3>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                  resource.type === 'link'
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-primary-100 text-primary-700'
                }`}
              >
                {resource.type === 'link' ? 'Link' : 'File'}
              </span>
            </div>
            {resource.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {resource.description}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              {resource.type === 'file'
                ? formatBytes(resource.file.size)
                : new URL(resource.url).hostname}
              {' '}&middot; {formatDate(resource.createdAt)}
            </p>
            {resource.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {resource.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mt-3">
              {resource.type === 'link' ? (
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Open link
                </a>
              ) : (
                <a
                  href={resource.file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </a>
              )}
              {actions}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      )
    }

    const isFiltering = searchTitle.trim().length > 0 || selectedTag.length > 0

    if (resources.length === 0 && isFiltering) {
      return (
        <div className="text-center py-12">
          <Search className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No resources match your search.</p>
        </div>
      )
    }

    if (resources.length === 0) {
      return (
        <div className="text-center py-12">
          <FolderOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <h3 className="text-lg font-semibold">Your Library is Empty</h3>
            </CardHeader>
            <CardBody>
              <p className="text-gray-600 mb-4">
                Save a document to build up your personal resource library.
              </p>
              <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
                Save Your First Resource
              </Button>
            </CardBody>
          </Card>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {resources.map((resource) =>
          renderResourceCard(
            resource,
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSharingResource(resource)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                aria-label="Share resource"
              >
                <Share2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setEditingResource(resource)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                aria-label="Edit resource"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDeletingResource(resource)}
                className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                aria-label="Delete resource"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )
        )}
      </div>
    )
  }

  const renderSharedContent = () => {
    if (sharedLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      )
    }

    if (sharedResources.length === 0) {
      return (
        <div className="text-center py-12">
          <Share2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">Nobody has shared a resource with you yet.</p>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sharedResources.map((resource) =>
          renderResourceCard(
            resource,
            <span className="text-xs text-gray-400">Shared by {resource.owner}</span>
          )
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Resources</h1>
            <p className="mt-2 text-gray-600">
              Documents and links you've saved, and material others have shared with you
            </p>
          </div>
          <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Save Resource
          </Button>
        </div>

        <div role="tablist" className="flex gap-1 border-b border-gray-200 mb-6">
          <button
            role="tab"
            aria-selected={activeTab === 'mine'}
            onClick={() => setActiveTab('mine')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'mine'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Library
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'shared'}
            onClick={() => setActiveTab('shared')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'shared'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Shared with me
          </button>
        </div>

        {activeTab === 'mine' ? (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  placeholder="Search by title..."
                  aria-label="Search resources by title"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              {availableTags.length > 0 && (
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  aria-label="Filter resources by tag"
                  className="border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All tags</option>
                  {availableTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {renderContent()}
          </>
        ) : (
          renderSharedContent()
        )}
      </div>

      <CreateResourceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onResourceCreated={handleResourceCreated}
      />

      {editingResource && (
        <EditResourceModal
          resource={editingResource}
          onClose={() => setEditingResource(null)}
          onResourceUpdated={handleResourceUpdated}
        />
      )}

      {sharingResource && (
        <ShareResourceModal
          resource={sharingResource}
          onClose={() => setSharingResource(null)}
          onResourceUpdated={handleResourceUpdated}
        />
      )}

      {deletingResource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete resource?</h2>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently delete &ldquo;{deletingResource.title}&rdquo;. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeletingResource(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={isDeleting}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ResourcesPage
